import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PublicLayout from '@/components/PublicLayout';
import SEO from '@/components/SEO';
import EventCard from '@/components/EventCard';
import { PageLoading } from '@/components/Loading';
import api from '@/lib/api';
import { zoneFromSlug, zoneSlug } from '@/lib/seo';
import { Building2, MapPin, ArrowRight } from 'lucide-react';

export default function ZonaDetail() {
  const { zonaSlug: slugParam } = useParams();
  const { t } = useTranslation();
  const [zones, setZones] = useState([]);
  const [data, setData] = useState({ eventi: [], locali: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => { api.get('/public/zones').then((r) => setZones(r.data?.zones || [])); }, []);

  const zona = useMemo(() => zoneFromSlug(slugParam, zones), [slugParam, zones]);

  useEffect(() => {
    if (!zona) return;
    setLoading(true);
    api.get(`/public/zone/${encodeURIComponent(zona)}`).then((r) => setData(r.data || { eventi: [], locali: [] })).finally(() => setLoading(false));
  }, [zona]);

  const localeMap = useMemo(() => Object.fromEntries((data.locali || []).map(l => [l.id, l])), [data.locali]);
  const baseUrl = typeof window !== 'undefined' ? `${window.location.protocol}//${window.location.host}` : '';

  if (zones.length && !zona) return (
    <PublicLayout>
      <div className="max-w-3xl mx-auto px-4 py-24 text-center">
        <h1 className="font-heading text-3xl mb-3">Zona non trovata</h1>
        <Link to="/" className="text-[color:var(--gold)]">Torna alla home</Link>
      </div>
    </PublicLayout>
  );

  return (
    <PublicLayout>
      <SEO
        title={`Serate a Roma — ${zona || ''} | Eventi e Locali`}
        description={`Tutte le serate, club ed eventi in zona ${zona} a Roma. Scopri i migliori locali e i prossimi appuntamenti.`}
        canonical={zona ? `${baseUrl}/serate-roma-${zoneSlug(zona)}` : ''}
      />
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-10 md:py-14 hero-overlay relative">
        <p className="text-xs tracking-[0.3em] uppercase text-[color:var(--gold)] mb-2 relative z-10">Roma • Nightlife • {zona}</p>
        <h1 data-testid="zone-page-title" className="font-heading text-4xl md:text-5xl relative z-10">{t('zone.title')} — <span className="text-[color:var(--gold)]">{zona}</span></h1>
        <p className="mt-4 text-[color:var(--text-muted)] max-w-3xl relative z-10">
          {t('zone.intro', { zone: zona })} Sfoglia gli eventi in programma e i locali più esclusivi della zona di {zona}, una delle aree più vive della movida romana.
        </p>
      </section>

      {loading ? <PageLoading /> : (
        <>
          <section className="max-w-6xl mx-auto px-4 sm:px-6 mt-2">
            <h2 className="font-heading text-2xl md:text-3xl mb-4">{t('zone.eventsInZone', { zone: zona })}</h2>
            {data.eventi.length === 0 ? (
              <div className="text-center py-12 rounded-2xl border border-[color:var(--border-c)] bg-[color:var(--surface)]">
                <p className="text-[color:var(--text-muted)]">{t('empty.events')}</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {data.eventi.map((e) => <EventCard key={e.id} evento={e} locale={localeMap[e.locale_id]} />)}
              </div>
            )}
          </section>

          {data.locali.length > 0 && (
            <section className="max-w-6xl mx-auto px-4 sm:px-6 mt-12">
              <h2 className="font-heading text-2xl md:text-3xl mb-4">{t('zone.venuesInZone')}</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {data.locali.map((l) => (
                  <Link key={l.id} to={`/locali/${l.slug}`} className="group rounded-xl border border-[color:var(--border-c)] bg-[color:var(--surface)] overflow-hidden hover:border-[color:var(--gold)]">
                    <div className="aspect-[16/9] bg-[color:var(--surface-2)]">
                      {l.gallery?.[0] || l.logo ? <img src={l.gallery?.[0] || l.logo} alt={l.nome} className="w-full h-full object-cover"/> : <div className="w-full h-full flex items-center justify-center"><Building2 className="w-10 h-10 text-[color:var(--gold)] opacity-40"/></div>}
                    </div>
                    <div className="p-4">
                      <h3 className="font-heading text-lg group-hover:text-[color:var(--gold)]">{l.nome}</h3>
                      <p className="text-xs text-[color:var(--text-muted)] flex items-center gap-1 mt-1"><MapPin className="w-3 h-3"/> {l.zona}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}

          {/* Other zones */}
          {zones.length > 1 && (
            <section className="max-w-6xl mx-auto px-4 sm:px-6 mt-16">
              <h2 className="font-heading text-xl mb-3 text-[color:var(--text-muted)]">Altre zone di Roma</h2>
              <div className="flex flex-wrap gap-2">
                {zones.filter(z => z !== zona).map((z) => (
                  <Link key={z} to={`/serate-roma-${zoneSlug(z)}`} className="px-4 py-2 rounded-full text-sm border border-[color:var(--border-c)] bg-[color:var(--surface)] hover:border-[color:var(--gold)] hover:text-[color:var(--gold)] flex items-center gap-2">
                    {z} <ArrowRight className="w-3 h-3"/>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </>
      )}
    </PublicLayout>
  );
}
